import { z } from "zod";

import { TransientRepositorySchema } from "./migration-instance";

export type AstGrepMatch<T> =
  | {
      type: "rule";
      severity: string;
      message: string;
      rule: string;
      env: T;
      id: number;
      range: [number, number, number, number];
    }
  | {
      type: "simple";
      env: T;
      id: number;
      range: [number, number, number, number];
    };

export interface StudioSession {
  id: string;
  name: string;
  state: StudioState;
  createdAt: Date;
  updatedAt: Date;
}

export enum StudioMode {
  JsAstGrep = "JsAstGrep",
  Config = "Config",
}

export const StudioSupportedLangsSchema = z.enum([
  "javascript",
  "typescript",
  "python",
  "go",
  "rust",
  "tsx",
  "java",
  "c_sharp",
  "cpp",
  "c",
  "html",
  "css",
  "kotlin",
  "php",
  "angular",
  "ruby",
  "elixir",
]);

export const AstGrepStrictnessEnumSchema = z.enum([
  "cst",
  "smart",
  "ast",
  "relaxed",
  "signature",
]);

export const StudioSourceSchema = z.object({
  name: z.string().optional(),
  source: z.string(),
  autoGenerated: z.boolean().optional(),
  id: z.number(),
});

export const StudioStateSchema = z.object({
  mode: z.nativeEnum(StudioMode),
  jsAstGrepCodemod: z.string(),
  rewrite: z.string(),
  config: z.string(),
  sources: z.array(StudioSourceSchema),
  strictness: AstGrepStrictnessEnumSchema,
  selector: z.string(),
  lang: StudioSupportedLangsSchema,
});

export type StudioSource = z.infer<typeof StudioSourceSchema>;
export type StudioState = z.infer<typeof StudioStateSchema>;
export type SupportedLang = z.infer<typeof StudioSupportedLangsSchema>;
export type AstGrepStrictness = z.infer<typeof AstGrepStrictnessEnumSchema>;

// Studio Package Creation Schemas
export const StudioConfigurationSchema = z.object({
  mode: z.nativeEnum(StudioMode),
  config: z.object({
    patterns: z.array(z.string()).optional(),
    language: z.string(),
    config: z.string(), // YAML config string
  }),
  jsAstGrepCodemod: z.object({
    script: z.string().min(1),
    language: z.string(),
  }),
  metadata: z.object({
    name: z.string().min(1).max(255),
    description: z.string().optional(),
    version: z.string().default("1.0.0"),
  }),
});

export const PackageMetadataSchema = z.object({
  name: z.string().min(1).max(255),
  displayName: z.string().min(1).max(255),
  description: z.string().optional(),
  author: z.string().min(1).max(255),
  license: z.string().optional(),
  repository: z.string().url().optional(),
  homepage: z.string().url().optional(),
  keywords: z.array(z.string()).default([]),
  category: z.string().optional(),
  visibility: z.enum(["public", "private", "org", "user"]).default("public"),
  access: z.enum(["public", "private"]).default("public"),
});

export const CreatePackageFromStudioSchema = z.object({
  studioConfig: StudioConfigurationSchema,
  packageMetadata: PackageMetadataSchema,
  repositories: z.array(TransientRepositorySchema).min(1),
});

export type StudioConfigurationType = z.infer<typeof StudioConfigurationSchema>;
export type PackageMetadataType = z.infer<typeof PackageMetadataSchema>;
export type CreatePackageFromStudioType = z.infer<
  typeof CreatePackageFromStudioSchema
>;
